import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';

/**
 * Reactions component with bubble popup selector:
 * - Shows current user reaction as outlined icon (or empty outline if none)
 * - Shows total counts on the right
 * - Hover (desktop) or click (mobile) opens bubble with 6 reaction options
 */
const Reactions = ({ postId, replyId, totalLikes, totalDislikes, userLikeValue, userDislikeValue }) => {
  const { accessToken } = useAuthStore();
  const { reactToPost, reactToReply, removeReaction } = usePostsStore();
  const [showBubble, setShowBubble] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  // Detect if device supports touch
  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleReaction = async (likeValue = null, dislikeValue = null) => {
    console.log('handleReaction called:', {
      likeValue,
      dislikeValue,
      userLikeValue,
      userDislikeValue,
      replyId,
      comparison: {
        likeMatch: likeValue && likeValue === userLikeValue,
        dislikeMatch: dislikeValue && dislikeValue === userDislikeValue
      }
    });

    // If both are null, clear the reaction
    if (likeValue === null && dislikeValue === null) {
      console.log('Clearing reaction (both null)');
      await removeReaction(accessToken, postId, replyId);
      setShowBubble(false);
      return;
    }

    // If clicking the same reaction, remove it
    if (
      (likeValue && likeValue === userLikeValue) ||
      (dislikeValue && dislikeValue === userDislikeValue)
    ) {
      console.log('Removing same reaction');
      await removeReaction(accessToken, postId, replyId);
      setShowBubble(false);
      return;
    }

    console.log('Setting new reaction');
    if (replyId) {
      await reactToReply(accessToken, postId, replyId, likeValue, dislikeValue);
    } else {
      await reactToPost(accessToken, postId, likeValue, dislikeValue);
    }
    setShowBubble(false);
  };

  // Determine which icon to show based on user's current reaction
  const getCurrentReactionIcon = () => {
    if (userLikeValue === 5) {
      return (
        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else if (userLikeValue === 3) {
      return (
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else if (userLikeValue === 1) {
      return (
        <svg className="w-4 h-4 text-green-700" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else if (userDislikeValue === 1) {
      return (
        <svg className="w-4 h-4 text-red-700 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else if (userDislikeValue === 3) {
      return (
        <svg className="w-5 h-5 text-red-600 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else if (userDislikeValue === 5) {
      return (
        <svg className="w-6 h-6 text-red-500 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    } else {
      // Empty outline thumbs up
      return (
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      );
    }
  };

  const handleToggleBubble = () => {
    if (isTouch) {
      setShowBubble(!showBubble);
    }
  };

  const handleMouseEnter = () => {
    if (!isTouch) {
      setShowBubble(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouch) {
      setShowBubble(false);
    }
  };

  return (
    <div className="flex items-center justify-between">
      {/* Left - Expandable Reaction Button */}
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleToggleBubble}
      >
        <div className={`h-12 flex items-center border-2 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 rounded-full transition-all duration-300 ${showBubble ? 'px-3' : 'w-12 justify-center'}`}>
          {!showBubble ? (
            // Collapsed: Show only current reaction icon
            <div className="flex items-center justify-center">
              {getCurrentReactionIcon()}
            </div>
          ) : (
            // Expanded: Show all reaction options
            <div className="flex items-center gap-2">
              {/* Current reaction indicator on the left - clickable to clear */}
              <button
                onClick={() => handleReaction(null, null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Clear reaction"
              >
                {getCurrentReactionIcon()}
              </button>

              {/* Divider */}
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>

              {/* Like Buttons */}
              <button
                onClick={() => handleReaction(5, null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Strong Like (+5)"
              >
                <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              <button
                onClick={() => handleReaction(3, null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Medium Like (+3)"
              >
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              <button
                onClick={() => handleReaction(1, null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Like (+1)"
              >
                <svg className="w-4 h-4 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              {/* Divider */}
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>

              {/* Dislike Buttons */}
              <button
                onClick={() => handleReaction(null, 1)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Dislike (-1)"
              >
                <svg className="w-4 h-4 text-red-700 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              <button
                onClick={() => handleReaction(null, 3)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Medium Dislike (-3)"
              >
                <svg className="w-5 h-5 text-red-600 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>

              <button
                onClick={() => handleReaction(null, 5)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Strong Dislike (-5)"
              >
                <svg className="w-6 h-6 text-red-500 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right - Total Counts */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
          <span className="font-medium text-primary">{totalLikes}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-red-600 transform rotate-180" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
          <span className="font-medium text-primary">{totalDislikes}</span>
        </div>
      </div>
    </div>
  );
};

export default Reactions;
